import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CDN_TYPE_GAME, imageUrlCDN } from 'utils';

import {
  CasinoApi,
  CasinoNewProvider,
  SiteConfigManager,
  useApplicationState,
  useCasinoNewState,
  useSettings,
} from '@atlas/core';
import { casinoRoutes, COMPONENT_TYPES } from '@atlas/routing';
import {
  BonusDetailsGamesMobileSkeleton,
  Button,
  CASINO_TILE_TYPES,
  CasinoGameTile,
  ChipsItem,
  Icon,
  Label,
  Loader,
} from '@atlas/ui';

import { GROUPS } from 'components/Pages/Casino/constants';

import { useBonusDetailContext } from './BonusDetailContext';
import {
  BonusEligibleGamesContext,
  useBonusEligibleGamesContext,
} from './BonusEligibleGamesContext';
import { BONUS_KIND } from './resolveBonusEntity';
import { useBonusActions } from './useBonusActions';

const { CASINO } = COMPONENT_TYPES;

const GAMES_PER_CHUNK = 25;
const INITIAL_CHUNKS = 2;

const LOAD_MORE_PROGRESS = 0.5;

const Skeleton = () => {
  return (
    <div className="bonus-eligible-games panel">
      <BonusDetailsGamesMobileSkeleton />
    </div>
  );
};

const BonusEligibleGamesProvider = ({ children }) => {
  const { entity } = useBonusDetailContext();
  const { lang } = useSettings();

  const gameIds = useMemo(() => entity?.gameIds || [], [entity?.gameIds]);
  const hasGameIds = !!gameIds.length;

  const [games, setGames] = useState([]);
  const [gamesResolved, setGamesResolved] = useState(!hasGameIds);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const chunkRef = useRef(0);
  const loadingRef = useRef(false);

  const fetchChunk = useCallback(
    chunk => {
      const start = chunk * GAMES_PER_CHUNK;
      const ids = gameIds.slice(start, start + GAMES_PER_CHUNK);

      if (!ids.length) return null;

      return CasinoApi.fetchCasinoGames({
        currentSlice: 0,
        group: GROUPS.CASINO,
        ids,
        lang,
      }).then(data => data?.games || []);
    },
    [gameIds, lang],
  );

  const loadInitialChunks = useCallback(() => {
    if (!lang || loadingRef.current) return;

    const requests = [];

    for (let chunk = 0; chunk < INITIAL_CHUNKS; chunk += 1) {
      const request = fetchChunk(chunk);

      if (!request) break;

      requests.push(request);
    }

    if (!requests.length) return;

    loadingRef.current = true;
    const loadedChunks = requests.length;

    Promise.all(requests)
      .then(chunks => {
        setGames(chunks.flat());
        chunkRef.current = loadedChunks - 1;
        setHasNext(loadedChunks * GAMES_PER_CHUNK < gameIds.length);
      })
      .finally(() => {
        loadingRef.current = false;
        setGamesResolved(true);
      });
  }, [fetchChunk, gameIds.length, lang]);

  const loadMore = useCallback(() => {
    if (!hasNext || loadingRef.current) return;

    const chunk = chunkRef.current + 1;
    const request = fetchChunk(chunk);

    if (!request) return;

    loadingRef.current = true;
    setLoadingMore(true);

    request
      .then(loadedGames => {
        setGames(prev => [...prev, ...loadedGames]);
        chunkRef.current = chunk;
        setHasNext((chunk + 1) * GAMES_PER_CHUNK < gameIds.length);
      })
      .finally(() => {
        loadingRef.current = false;
        setLoadingMore(false);
      });
  }, [fetchChunk, gameIds.length, hasNext]);

  useEffect(() => {
    if (!lang || !gameIds.length || games.length) return;

    loadInitialChunks();
  }, [gameIds.length, games.length, lang, loadInitialChunks]);

  const value = useMemo(
    () => ({ gameIds, games, gamesResolved, hasNext, loadMore, loadingMore }),
    [gameIds, games, gamesResolved, hasNext, loadMore, loadingMore],
  );

  return (
    <BonusEligibleGamesContext.Provider value={value}>
      {children}
    </BonusEligibleGamesContext.Provider>
  );
};

BonusEligibleGamesProvider.propTypes = {
  children: PropTypes.node,
};

const Games = () => {
  const history = useHistory();
  const { authenticated } = useApplicationState();
  const casinoGamePath = casinoRoutes.game;
  const casinoPath = SiteConfigManager.getComponentPath(CASINO);
  const type = SiteConfigManager.getConfig(
    'config.config.general.mobileCasinoGameTileView',
    CASINO_TILE_TYPES.TYPE_5,
  );

  const {
    addToFavorites,
    favoriteGames: { data: favoriteGames },
    identifyFavoriteGames,
    removeFromFavorites,
  } = useCasinoNewState();
  const [activeOverlayId, setActiveOverlayId] = useState(null);

  const { games } = useBonusEligibleGamesContext();

  useEffect(() => {
    if (!authenticated || !games.length) return;

    identifyFavoriteGames(games.map(game => game.id));
  }, [authenticated, games, identifyFavoriteGames]);

  const tiles = useMemo(() => {
    return games.map(game => {
      const image = game.iconUrl
        ? imageUrlCDN(game.iconUrl, CDN_TYPE_GAME)
        : '../static/assets/images/bonus-default.png';

      return {
        favoriteId: game.id,
        id: String(game.id ?? game.gameRelationId),
        image,
        name: game.name,
        provider: game.providerName,
      };
    });
  }, [games]);

  const toggleFavorite = id => {
    if (!authenticated) return;

    if (favoriteGames.includes(id)) {
      removeFromFavorites(id);
    } else {
      addToFavorites(id);
    }
  };

  const toggleOverlay = id => {
    setActiveOverlayId(prevId => (prevId === id ? null : id));
  };

  const handlePlayClick = id => {
    const newGameUrl = new URL(`${window.location.origin}${casinoPath}/${casinoGamePath}/${id}`);
    newGameUrl.searchParams.set('from', window.location.pathname + window.location.search);

    history.push(newGameUrl.pathname + newGameUrl.search);
  };

  return tiles.map(game => {
    if (!game?.id) return null;

    return (
      <CasinoGameTile
        favorite={{
          onClick: () => toggleFavorite(game.favoriteId),
          state: favoriteGames.includes(game.favoriteId),
        }}
        image={game.image}
        isMobile
        key={game.id}
        name={game.name}
        onClickPlay={() => handlePlayClick(game.id)}
        provider={game.provider}
        showOverlay={activeOverlayId === game.id}
        size="S"
        toggleShowOverlay={() => toggleOverlay(game.id)}
        type={type}
      />
    );
  });
};

const BonusEligibleGamesContent = () => {
  const { entity, kind, loading } = useBonusDetailContext();
  const { gameIds, games, gamesResolved, hasNext, loadMore, loadingMore } =
    useBonusEligibleGamesContext();
  const history = useHistory();
  const actions = useBonusActions();

  const casinoPath = SiteConfigManager.getComponentPath(CASINO);

  const totalGamesCount = entity?.gamesCount ?? gameIds.length;
  // No-deposit freespins are playable right away, so only DEPOSIT freespins are locked while
  // Available. Regular bonuses keep the status-based Available lock.
  const isFreeSpin = kind === BONUS_KIND.FREESPIN;
  const isLocked =
    ['ACTIVATION_READY', 'ACTIVE', 'NEW'].includes(entity?.status) &&
    (!isFreeSpin || Boolean(entity?.isDepositLike));
  const isPendingActivation = entity?.status === 'PENDING_ACTIVATION';

  const depositLockLabel = entity?.isDepositLike ? 'make_a_deposit' : 'eligible_games_to_activate';
  const lockedOverlayLabel = isFreeSpin ? 'to_play_make_a_deposit' : depositLockLabel;

  const gamesCategoryId = SiteConfigManager.getConfig(
    'config.config.general.eligibleGamesCategoryId',
  );

  const isViewAllVisible = !!gamesCategoryId && !isLocked;

  const handleViewAll = () => history.push(`${casinoPath}?category=${gamesCategoryId}`);

  const handleScroll = useCallback(
    event => {
      if (isLocked || !hasNext || loadingMore) return;

      const { clientWidth, scrollLeft, scrollWidth } = event.currentTarget;
      const maxScroll = scrollWidth - clientWidth;

      if (maxScroll > 0 && scrollLeft / maxScroll >= LOAD_MORE_PROGRESS) loadMore();
    },
    [hasNext, isLocked, loadMore, loadingMore],
  );

  if (loading || !gamesResolved) return <Skeleton />;
  if (!games.length) return null;

  return (
    <div className="bonus-eligible-games panel">
      <div className="bonus-eligible-games__header">
        <div className="bonus-eligible-games__name">
          <Icon className="bonus-eligible-games__icon" name="casino" />
          <div className="bonus-eligible-games__title">
            <Label message="eligible_games_title" />
          </div>
        </div>

        {/* Freespins target their own eligible game(s) — the casino-wide "View all" is bonus-only. */}
        {isViewAllVisible && !isFreeSpin && (
          <ChipsItem
            className="bonus-eligible-games__view-all"
            label={
              <div className="bonus-eligible-games__title__view">
                <Label className="title__text" message="view_all" />({totalGamesCount})
              </div>
            }
            onClick={handleViewAll}
          />
        )}
      </div>

      <div
        className={cx(
          'bonus-eligible-games-list',
          { 'is-locked': isLocked },
          { 'pending-activation': isPendingActivation },
        )}
        onScroll={handleScroll}
      >
        {isLocked && (
          <div className="bonus-eligible-games-list__locked-overlay">
            <Icon name="lock" />
            <Label className="locked-overlay__label" message={lockedOverlayLabel} />
          </div>
        )}
        <Games />
        {loadingMore && (
          <div className="bonus-eligible-games-list__loader">
            <Loader />
          </div>
        )}
      </div>
      {isPendingActivation && (
        <Button label="activate_and_play" onClick={actions.activate} size="big" type="primary" />
      )}
    </div>
  );
};

const BonusEligibleGames = () => (
  <CasinoNewProvider group={GROUPS.CASINO} skipDataLoad>
    <BonusEligibleGamesProvider>
      <BonusEligibleGamesContent />
    </BonusEligibleGamesProvider>
  </CasinoNewProvider>
);

export default BonusEligibleGames;
